import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  UseQueryOptions,
  UseInfiniteQueryOptions,
  InfiniteData,
} from '@tanstack/react-query';
import {
  initiativesApi,
  ApiInitiative,
  InitiativesResponse,
  InitiativesQuery,
  CreateInitiativeRequest,
  UpdateInitiativeRequest,
} from '../api';
import * as _ from 'es-toolkit';

export const initiativeQueryKeys = {
  all: ['initiatives'] as const,
  lists: () => [...initiativeQueryKeys.all, 'list'] as const,
  list: (filters: string | Record<string, any>) =>
    [...initiativeQueryKeys.lists(), { filters }] as const,
  details: () => [...initiativeQueryKeys.all, 'detail'] as const,
  detail: (id: number) => [...initiativeQueryKeys.details(), id] as const,
  templates: () => [...initiativeQueryKeys.all, 'templates'] as const,
  templateList: (filters: string | Record<string, any>) =>
    [...initiativeQueryKeys.templates(), { filters }] as const,
  adminLists: () => ['admin-initiatives', 'list'] as const,
  adminList: (filters: string | Record<string, any>) =>
    [...initiativeQueryKeys.adminLists(), { filters }] as const,
};

export function useInitiatives(
  query?: InitiativesQuery,
  options?: Partial<
    UseInfiniteQueryOptions<
      InitiativesResponse,
      Error,
      InfiniteData<InitiativesResponse>,
      any,
      string
    >
  > & { accessToken?: string | null }
) {
  const { accessToken, ...queryOptions } = options ?? {};
  return useInfiniteQuery<InitiativesResponse>({
    queryKey: accessToken
      ? [...initiativeQueryKeys.list(query || {}), { accessToken }]
      : initiativeQueryKeys.list(query || {}),
    queryFn: async ({ pageParam }) => {
      const response = await initiativesApi.getInitiatives(
        _.merge(query || {}, pageParam ? { nextCursor: pageParam } : {}),
        accessToken ? { accessToken } : undefined
      );
      return response;
    },
    staleTime: 1000 * 10,
    getNextPageParam: (lastPage: any) => lastPage.pagination?.nextCursor || undefined,
    initialPageParam: '',
    // @ts-ignore
    ...(queryOptions as any),
  });
}

export function useInitiative(
  id: number,
  options?: Partial<UseQueryOptions<ApiInitiative>> & { accessToken?: string | null }
) {
  const { accessToken, ...queryOptions } = options ?? {};
  return useQuery({
    queryKey: accessToken
      ? [...initiativeQueryKeys.detail(id), { accessToken }]
      : initiativeQueryKeys.detail(id),
    queryFn: async () =>
      initiativesApi.getInitiative(id, accessToken ? { accessToken } : undefined),
    enabled: !!id && !isNaN(id),
    staleTime: 1000 * 10,
    ...queryOptions,
  });
}

export function useCreateInitiative(options?: { invalidateOnSuccess?: boolean }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateInitiativeRequest) => {
      return await initiativesApi.createInitiative(data);
    },
    onMutate: async (data) => {
      // Cancel all outgoing refetches for initiatives (fuzzy match)
      await queryClient.cancelQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return key === 'initiatives' || key === 'admin-initiatives';
        },
      });

      // Snapshot previous values (fuzzy match all initiative queries)
      const previousQueries = queryClient.getQueriesData({
        predicate: (query) => {
          const key = query.queryKey[0];
          return key === 'initiatives' || key === 'admin-initiatives';
        },
      });

      // Create optimistic initiative with temporary ID
      const optimisticInitiative: any = {
        id: Date.now(), // Temporary ID
        name: data.name,
        description: data.description || '',
        content: data.content || '',
        type: data.type,
        status: data.status || 'draft',
        visibility: data.visibility || 'private',
        tags: data.tags || [],
        systemTags: [],
        data: data.data || {},
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        deleted: false,
      };

      // Optimistically add to all initiative list queries (fuzzy)
      queryClient.setQueriesData(
        {
          predicate: (query) => {
            const key = query.queryKey[0];
            return key === 'initiatives' || key === 'admin-initiatives';
          },
        },
        (old: any) => {
          if (!old) return old;

          // Handle infinite query structure - add to first page
          if (old.pages) {
            const newPages = [...old.pages];
            if (newPages[0]) {
              newPages[0] = {
                ...newPages[0],
                initiatives: [optimisticInitiative, ...(newPages[0].initiatives || [])],
              };
            }
            return {
              ...old,
              pages: newPages,
            };
          }

          // Handle regular array response
          if (Array.isArray(old)) {
            return [optimisticInitiative, ...old];
          }

          // Handle object with initiatives array
          if (old.initiatives && Array.isArray(old.initiatives)) {
            return {
              ...old,
              initiatives: [optimisticInitiative, ...old.initiatives],
            };
          }

          return old;
        }
      );

      return { previousQueries, optimisticInitiative };
    },
    onError: (err, data, context) => {
      // Rollback all queries on error
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: (newInitiative, data, context) => {
      // Replace optimistic initiative with real one in all queries (fuzzy)
      queryClient.setQueriesData(
        {
          predicate: (query) => {
            const key = query.queryKey[0];
            return key === 'initiatives' || key === 'admin-initiatives';
          },
        },
        (old: any) => {
          if (!old) return old;

          const optimisticId = context?.optimisticInitiative?.id;

          // Handle infinite query structure
          if (old.pages) {
            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                initiatives: page.initiatives?.map((initiative: any) =>
                  initiative.id === optimisticId ? newInitiative : initiative
                ),
              })),
            };
          }

          // Handle regular array response
          if (Array.isArray(old)) {
            return old.map((initiative: any) =>
              initiative.id === optimisticId ? newInitiative : initiative
            );
          }

          // Handle object with initiatives array
          if (old.initiatives && Array.isArray(old.initiatives)) {
            return {
              ...old,
              initiatives: old.initiatives.map((initiative: any) =>
                initiative.id === optimisticId ? newInitiative : initiative
              ),
            };
          }

          return old;
        }
      );

      // Set the detail query for the new initiative
      queryClient.setQueryData(initiativeQueryKeys.detail(newInitiative.id), newInitiative);

      // Only invalidate if explicitly requested
      if (options?.invalidateOnSuccess) {
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return key === 'initiatives' || key === 'admin-initiatives';
          },
        });
      }
    },
  });
}

export function useUpdateInitiative(options?: { invalidateOnSuccess?: boolean }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateInitiativeRequest }) => {
      return await initiativesApi.updateInitiative(id, data);
    },
    onMutate: async ({ id, data }) => {
      // Cancel all outgoing refetches for initiatives (fuzzy match)
      await queryClient.cancelQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return key === 'initiatives' || key === 'admin-initiatives';
        },
      });

      // Snapshot previous values
      const previousInitiative = queryClient.getQueryData(initiativeQueryKeys.detail(id));
      const previousQueries = queryClient.getQueriesData({
        predicate: (query) => {
          const key = query.queryKey[0];
          return key === 'initiatives' || key === 'admin-initiatives';
        },
      });

      // Optimistically update the detail query
      queryClient.setQueryData(initiativeQueryKeys.detail(id), (old: any) => {
        if (!old) return old;
        return { ...old, ...data, updatedAt: new Date().toISOString() };
      });

      // Optimistically update all queries (fuzzy match)
      queryClient.setQueriesData(
        {
          predicate: (query) => {
            const key = query.queryKey[0];
            return key === 'initiatives' || key === 'admin-initiatives';
          },
        },
        (old: any) => {
          if (!old) return old;

          // Handle infinite query structure
          if (old.pages) {
            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                initiatives: page.initiatives?.map((initiative: ApiInitiative) =>
                  initiative.id === id
                    ? { ...initiative, ...data, updatedAt: new Date().toISOString() }
                    : initiative
                ),
              })),
            };
          }

          // Handle regular array response
          if (Array.isArray(old)) {
            return old.map((initiative: ApiInitiative) =>
              initiative.id === id
                ? { ...initiative, ...data, updatedAt: new Date().toISOString() }
                : initiative
            );
          }

          // Handle object with initiatives array
          if (old.initiatives && Array.isArray(old.initiatives)) {
            return {
              ...old,
              initiatives: old.initiatives.map((initiative: ApiInitiative) =>
                initiative.id === id
                  ? { ...initiative, ...data, updatedAt: new Date().toISOString() }
                  : initiative
              ),
            };
          }

          return old;
        }
      );

      return { previousInitiative, previousQueries };
    },
    onError: (err, { id }, context) => {
      // Rollback detail query on error
      if (context?.previousInitiative) {
        queryClient.setQueryData(initiativeQueryKeys.detail(id), context.previousInitiative);
      }

      // Rollback all queries on error
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: (updatedInitiative, { id }) => {
      // Update detail query with server response
      queryClient.setQueryData(initiativeQueryKeys.detail(id), updatedInitiative);

      // Update all queries with server response (fuzzy match)
      queryClient.setQueriesData(
        {
          predicate: (query) => {
            const key = query.queryKey[0];
            return key === 'initiatives' || key === 'admin-initiatives';
          },
        },
        (old: any) => {
          if (!old) return old;

          // Handle infinite query structure
          if (old.pages) {
            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                initiatives: page.initiatives?.map((initiative: ApiInitiative) =>
                  initiative.id === id ? updatedInitiative : initiative
                ),
              })),
            };
          }

          // Handle regular array response
          if (Array.isArray(old)) {
            return old.map((initiative: ApiInitiative) =>
              initiative.id === id ? updatedInitiative : initiative
            );
          }

          // Handle object with initiatives array
          if (old.initiatives && Array.isArray(old.initiatives)) {
            return {
              ...old,
              initiatives: old.initiatives.map((initiative: ApiInitiative) =>
                initiative.id === id ? updatedInitiative : initiative
              ),
            };
          }

          return old;
        }
      );

      // Only invalidate if explicitly requested
      if (options?.invalidateOnSuccess) {
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return key === 'initiatives' || key === 'admin-initiatives';
          },
        });
      }
    },
  });
}

export function useBulkUpdateInitiatives() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      initiativeIds,
      updates,
    }: {
      initiativeIds: number[];
      updates: { status?: string };
    }) => {
      return initiativesApi.bulkUpdateInitiatives(initiativeIds, updates);
    },
    onMutate: async ({ initiativeIds, updates }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return key === 'initiatives' || key === 'admin-initiatives';
        },
      });

      // Snapshot previous values
      const previousQueries = queryClient.getQueriesData({
        predicate: (query) => {
          const key = query.queryKey[0];
          return key === 'initiatives' || key === 'admin-initiatives';
        },
      });

      // Optimistically update all matching queries
      queryClient.setQueriesData(
        {
          predicate: (query) => {
            const key = query.queryKey[0];
            return key === 'initiatives' || key === 'admin-initiatives';
          },
        },
        (old: any) => {
          if (!old) return old;

          // Handle infinite query structure
          if (old.pages) {
            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                initiatives: page.initiatives?.map((initiative: any) => {
                  if (initiativeIds.includes(Number(initiative.id))) {
                    return {
                      ...initiative,
                      status: updates.status ?? initiative.status,
                      updatedAt: new Date().toISOString(),
                    };
                  }
                  return initiative;
                }),
              })),
            };
          }

          // Handle regular array
          if (Array.isArray(old)) {
            return old.map((initiative: any) => {
              if (initiativeIds.includes(Number(initiative.id))) {
                return {
                  ...initiative,
                  status: updates.status ?? initiative.status,
                  updatedAt: new Date().toISOString(),
                };
              }
              return initiative;
            });
          }

          // Handle object with initiatives array
          if (old.initiatives && Array.isArray(old.initiatives)) {
            return {
              ...old,
              initiatives: old.initiatives.map((initiative: any) => {
                if (initiativeIds.includes(Number(initiative.id))) {
                  return {
                    ...initiative,
                    status: updates.status ?? initiative.status,
                    updatedAt: new Date().toISOString(),
                  };
                }
                return initiative;
              }),
            };
          }

          return old;
        }
      );

      return { previousQueries };
    },
    onError: (_err, _variables, context) => {
      // Rollback to previous state on error
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return key === 'initiatives' || key === 'admin-initiatives';
        },
      });
    },
  });
}

export function useDeleteInitiative(options?: { invalidateOnSuccess?: boolean }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await initiativesApi.deleteInitiative(id);
      return id;
    },
    onMutate: async (id) => {
      // Cancel outgoing refetches (fuzzy match)
      await queryClient.cancelQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return key === 'initiatives' || key === 'admin-initiatives';
        },
      });

      // Snapshot previous values
      const previousQueries = queryClient.getQueriesData({
        predicate: (query) => {
          const key = query.queryKey[0];
          return key === 'initiatives' || key === 'admin-initiatives';
        },
      });

      // Optimistically remove from all queries (fuzzy match)
      queryClient.setQueriesData(
        {
          predicate: (query) => {
            const key = query.queryKey[0];
            return key === 'initiatives' || key === 'admin-initiatives';
          },
        },
        (old: any) => {
          if (!old) return old;

          // Handle infinite query structure
          if (old.pages) {
            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                initiatives:
                  page.initiatives?.filter((initiative: any) => initiative.id !== id) || [],
              })),
            };
          }

          // Handle regular array
          if (Array.isArray(old)) {
            return old.filter((initiative: any) => initiative.id !== id);
          }

          // Handle object with initiatives array
          if (old.initiatives && Array.isArray(old.initiatives)) {
            return {
              ...old,
              initiatives: old.initiatives.filter((initiative: any) => initiative.id !== id),
            };
          }

          return old;
        }
      );

      return { previousQueries };
    },
    onError: (err, id, context) => {
      // Rollback on error
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: (id) => {
      // Only invalidate if explicitly requested
      if (options?.invalidateOnSuccess) {
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return key === 'initiatives' || key === 'admin-initiatives';
          },
        });
      }
    },
  });
}
